<script setup>

import { KNOLIA } from "@/knolia";
import { ref, onMounted, onUpdated } from "vue";
import { Gems } from "../gems";
import ActivitiesJobRow from "../components/ActivitiesJobRow.vue";
import DropZone from "../components/DropZone.vue";
import BaseSelect from "../components/BaseSelect.vue";
import { useDragAndDrop } from "../store/modules/drag-and-drop";

const subordinates = ref([])
const activities = ref([])
const extraJobActivities = ref([])
const ratedActivities = ref([])
const subordinateActivities = ref([])
const userData = ref([])
const dropZone = ref(null)
const currentSubordinate = ref({ name: null, id: null })
const txtUser = ref('')
const txtActivities = ref('')
const dragAndDrop = useDragAndDrop()
const showOnlyRatedActivities = ref(false) 


async function GetSubordinateActivities(subordinateName, subordinateId) {
    currentSubordinate.value.name = subordinateName
    currentSubordinate.value.id = subordinateId

    KNOLIA.SetRequestedUserId(subordinateId)

    const response = await KNOLIA.CallBackEnd('post', 'get-subordinate-activities', { params: { user_id: subordinateId } })
    extraJobActivities.value = response.data.extra_job_activities ?? []
    subordinateActivities.value = response.data.user_activities ?? []
    ratedActivities.value = response.data.rated_activities ?? []
    userData.value = response.data.user_data ?? []

    SetActivitiesAccess()

    dragAndDrop.setDropFunction(function () {
        const activity = dragAndDrop.DraggedData.activity
        activity.active = true
        activity.is_function_activity = false
        ControlActivityStatus(activity)
    }, dropZone.value)
    
    FilterList(activities.value, txtActivities.value, 'designation', { forActivities: true })
}

function SetActivitiesAccess() {
    activities.value.forEach(activity => {
        document.getElementById(`activity${activity.id}`).classList.remove('unavailable')
        activity.unavailable = false

        subordinateActivities.value.forEach(JobActivity => {
            if(activity.id == JobActivity.id) {
                activity.unavailable = true
                activity.showChildren = false
            }
        })

        extraJobActivities.value.forEach(outsideJobActivity => {
            if(activity.id == outsideJobActivity.id) {
                activity.unavailable = true
                activity.showChildren = false
            }
        })
    })
}

async function RemoveActivityFromUser(activity){
    activity.active = false; 
    ControlActivityStatus(activity)
}

async function ControlActivityStatus(activity) {
    await KNOLIA.CallBackEnd('post', 'control-activity-status', { params: { activity } })
    GetSubordinateActivities(currentSubordinate.value.name, currentSubordinate.value.id)
}

async function SelectTaskAssignment(activity, task) {
    if(!activity.active) {
        Gems.Telegram('Habilite a atividade antes', 'tele-not-allowed')
        return;
    }

    await KNOLIA.CallBackEnd('post', 'set-operations&get=change-user-task-participation', { params: { activity, task, currentSubordinate: currentSubordinate.value } })
}

async function SelectActivityAssignment(activity) {
    if(!activity.active) {
        Gems.Telegram('Habilite a atividade antes', 'tele-not-allowed')
        return;
    }

    await KNOLIA.CallBackEnd('post', 'set-operations&get=change-user-activity-participation', { params: { activity, currentSubordinate: currentSubordinate.value } })
}

function FilterList(list, search, field, options = {}) {
    const forActivities = !!options.forActivities
    const normalize = (s) => (s || '').toString().normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()
    const searchNormalized = normalize(search)

    list.forEach(row => {
        const value = normalize(row[field])
        const matchesSearch = !searchNormalized || value.includes(searchNormalized)

        let passesRated = true
        if (forActivities && showOnlyRatedActivities.value) {
            const ind = ratedActivities.value.findIndex(elem => elem.id == row.id)
            passesRated = !!ratedActivities.value[ind]
        }

        row.showSelf = matchesSearch && passesRated
    })
}

function ShowSubordinatesActivities(subordinate) {
    if(currentSubordinate.value.id == null)
    showOnlyRatedActivities.value = true
    GetSubordinateActivities(subordinate.name, subordinate.user_id)
}

function SecondGroupTitleResize() {
    const activititiesControlRightPanelHeight = document.getElementById('activities-control-right-panel').offsetHeight
    document.getElementById('activities-control-left-panel').style.height = activititiesControlRightPanelHeight + 'px'
    if(window.innerWidth < 576) // bootstrap breakpoint
        document.getElementById('activities-control-left-panel').style.height = ''
}

onMounted(async () => {
    const responseSubordinates = await KNOLIA.CallBackEnd('get', 'get-operations', {
        params: {
            get: 'subordinates'
        }
    }) 
    subordinates.value = responseSubordinates.data

    const responseActivities = await KNOLIA.CallBackEnd('post', 'get-activities-list')
    activities.value = responseActivities.data.filter(activity => activity.included)

    SecondGroupTitleResize()
    window.addEventListener('resize', () => {
        SecondGroupTitleResize()
    })
})

onUpdated(() => {
    document.querySelectorAll('.activity-status').forEach(elem => {
        Gems.Tooltip(elem, 'Ativar/Desativar atividade', 'tooltip')
    })
})

</script>

<template>
    <div class="page-container">
        <div class="mb-2 h-50">
            <div class="div-default-title pb-1 cursor-default div-title-with-search-bar">
                <span>Subordinados</span>
                <input type="text" class="ddl-alt-padding input-txt" placeholder="Pesquisar subordinado..." v-model="txtUser" @keyup="FilterList(subordinates, txtUser, 'name')">
            </div>

            <div class="div-subordinates-container group-frame scrollbar" id="subordinates-group-frame">
                <div class="div-row cursor-pointer" :class="{'bg-alt-line user-selected': currentSubordinate.id == subordinate.user_id }"
                    v-for="subordinate in subordinates" :key="subordinate.user_id"
                    @click="ShowSubordinatesActivities(subordinate)" v-show="subordinate.showSelf == null ? true : subordinate.showSelf">
                    <span class="line-clamp line-clamp-1" @mouseover="Gems.TooltipIfNeeded($event.target)">{{ subordinate.name }}</span>
                </div>
            </div>
        </div>

        <div class="row m-0 h-50">
            <div class="col-12 col-sm-6 col-md-5 p-0 m-0 pe-sm-3 pe-md-3 h-100">
                <div class="div-default-title pt-1 pb-1 cursor-default align-content-end" id="activities-control-left-panel">
                    <span class="line-clamp line-clamp-1">Atividades</span>
                </div>

                <div class="group-frame scrollbar">
                    
                    <div v-if="currentSubordinate.id">
                        <span class="job-title">{{ userData.job_desig ? `Da função ${userData.job_desig}` : '[Sem função definida]' }}</span>
                        <div v-for="activity in subordinateActivities" :key="activity.id">
                            <div class="div-row div-activity-row justify-content-between">
                                <div class="div-activity-row-left">
                                    <i class="fas show-children cursor-pointer" :class="{ 'fa-chevron-down': !activity.showChildren, 'fa-chevron-up': activity.showChildren }"
                                        @click="activity.showChildren = !activity.showChildren"></i>
                                    
                                        <input class="activity-status cursor-pointer" type="checkbox" v-model="activity.active" @change="ControlActivityStatus(activity)">
                                    
                                    <label class="w-100 line-clamp line-clamp-1">{{ activity.designation }}</label>
                                </div>

                                <base-select class="cursor-pointer ddl-alt-padding" v-model="activity.participation_level" @change="SelectActivityAssignment(activity)">
                                    <option :value="0">[sem nível determinado]</option>
                                    <option v-for="assignment in activity.participation_levels" :key="assignment.level" :value="assignment.level">
                                        {{ assignment.designation }}
                                    </option>
                                </base-select>
                            </div>

                            <div v-show="activity.showChildren" class="div-task-container">
                                <div class="div-row div-child" v-for="task in activity.children"
                                    :key="task.id">
                                    <div class="line-clamp line-clamp-1 cursor-default" @mouseover="Gems.TooltipIfNeeded($event.target)">{{ task.designation }}</div>

                                    <base-select class="ddl-alt-padding" v-model="task.participation_level" @change="SelectTaskAssignment(activity, task)">
                                        <option :value="0">[sem nível determinado]</option>
                                        <option v-for="assignment in task.participation_levels" :key="assignment.level" :value="assignment.level">
                                            {{ assignment.designation }}
                                        </option>
                                    </base-select>
                                </div>
                            </div>
                        </div>

                        <span class="span-outside-job-title">Adicionadas do posto de trabalho</span>

                        <div ref="dropZone">
                            <DropZone id="drop-zone"/>
                        </div>

                        <div v-for="activity in extraJobActivities" :key="activity.id">
                            <div class="div-row justify-content-between">
                                <div class="div-activity-row-left">
                                    <i class="fas show-children cursor-pointer" :class="{ 'fa-chevron-down': !activity.showChildren, 'fa-chevron-up': activity.showChildren }"
                                        @click="activity.showChildren = !activity.showChildren"></i>
                                    
                                     <i @click="RemoveActivityFromUser(activity)" class="fa fa-times cursor-pointer" ></i>

                                    <label>{{ activity.designation }}</label>
                                </div>

                                <base-select class="cursor-pointer ddl-alt-padding" v-model="activity.participation_level" @change="SelectActivityAssignment(activity)">
                                    <option :value="0">[sem nível determinado]</option>
                                    <option v-for="assignment in activity.participation_levels" :key="assignment.level" :value="assignment.level">
                                        {{ assignment.designation }}
                                    </option>
                                </base-select>
                            </div>

                            <div v-show="activity.showChildren" class="div-task-container">
                                <div class="div-row div-child" v-for="task in activity.children" :key="task.id">
                                    <div class="line-clamp line-clamp-1 cursor-default" @mouseover="Gems.TooltipIfNeeded($event.target)">{{ task.designation }}</div>

                                    <base-select class="cursor-pointer ddl-alt-padding" v-model="task.participation_level" @change="SelectTaskAssignment(activity, task)">
                                        <option :value="0">[sem nível determinado]</option>
                                        <option v-for="assignment in task.participation_levels" :key="assignment.level" :value="assignment.level">
                                            {{ assignment.designation }}
                                        </option>
                                    </base-select>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-else class="div-no-subordinate-selected">
                        Selecione um subordinado
                    </div>
                </div>
            </div>

            <div class="col-12 col-sm-6 col-md-7 p-0 m-0 h-100">
                <div class="div-default-title div-title-with-search-bar pt-1 pb-1 cursor-default" id="activities-control-right-panel">
                    <span class="align-content-end">Atividades <i class="fa fa-info-circle" @mouseover="Gems.Tooltip($event.target, 'Lista das atividades para atribuir como adicionadas do posto de trabalho.', 'tooltip')"></i></span>
                    
                    <div class="div-filter-activities"> 
                        <div class="div-chk">
                            <input type="checkbox" name="only-rated-activities" class="cursor-pointer" v-model="showOnlyRatedActivities" @change="FilterList(activities, txtActivities, 'designation', { forActivities: true })">
                            <label for="only-rated-activities">Classificadas </label>
                            <i class="fa fa-info-circle" @mouseover="Gems.Tooltip($event.target, 'Lista apenas as atividades com competências já classificadas.', 'tooltip')"></i>
                        </div>
                        <input type="text" class="ddl-alt-padding input-txt" placeholder="Pesquisar atividade..." v-model="txtActivities" @keyup="FilterList(activities, txtActivities, 'designation', { forActivities: true })">
                    </div>
                </div>

                <div class="group-frame scrollbar">                    
                    <ActivitiesJobRow :activities="activities" :draggable="true" :user-activity-control="true" @remove-task="RemoveTask($event)"/>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

.page-container {
    height: 100vh;
}

.div-title-with-search-bar {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
}

.div-filter-activities {
    display: flex;
    gap: 10px;
}

.div-chk {
    display: flex;
    gap: 10px;
    align-items: center;
    font-size: 1rem;
    font-weight: normal !important;
}

.div-title-with-search-bar .input-txt {
    font-size: 0.9rem !important;
    width: 35%;
}

#activities-control-right-panel .input-txt {
    width: 55%;
}

.job-title,
.span-outside-job-title {
    font-size: 1.2em;
    font-weight: 600;
    padding-left: 10px;
    display: block;
    background: var(--default-app-alt-line-color);
    padding: 6px 10px;
    cursor: default;

}

.job-title {
    margin-bottom: 3px;
}

.span-outside-job-title {
    margin-top: 15px;
}

.group-frame {
    padding: 6px;
    height: calc(100% - 40px);;
    text-align: left;
}

.div-row {
    padding: 7px 10px;
    align-items: center;
    border-radius: 25px;
    display: flex;
    gap: 10px;
    border: 1px solid transparent;
}

.div-activity-row {
    display: grid;
    grid-template-columns: 68% 30%;
}

.div-activity-row-left {
    display: flex;
    align-items: center;
    gap: 10px;
}

.div-child {
    justify-content: space-between;
    margin-top: 0px !important;
    padding: 0px;
}

.div-child:not(:first-child) {
    margin-top: 5px !important;
}

.div-row:not(:first-child),
.job-title:last-of-type {
    margin-top: 0px;
}

.div-activity,
.div-task-container {
    height: 35px;
    padding-left: 32px;
    padding-right: 0px;
}

.div-task-container {
    height: auto;
    padding-left: 53px;
    padding-right: 10px;
}

.div-activity-type-title {
    display: flex;
    flex-direction: column;
    margin-top: 10px;
    margin-bottom: 3px;
    opacity: .8;
}

.span-line {
    width: 100%;
    border-bottom: 1px solid;
}

.span-activity-type {
    padding: 3px 10px;
    cursor: default;
}

.user-selected {
    color: var(--knolia-yellow-white);
    border: 1px solid var(--knolia-yellow-white);
}

.div-no-subordinate-selected {
    text-align: center;
    margin-top: 25%;
    font-size: 2em;
    font-weight: bold;
}

@media screen and (max-width: 765px) {

    .page-container {
        overflow: auto;
    }

    .div-default-title {
        font-size: 1em;
    }

    .div-chk {
        font-size: 0.9em;
    }

    .group-frame {
        padding: 0px;
        height: calc(100% - 30px);
    }

    .div-subordinates-container .div-row {
        padding: 5px;
    }

    .div-task-container .div-row {
        justify-content: space-between;
    }

    .div-row {
        border-radius: 0px;
        margin: 0px !important;
        padding: 2.5px 15px;
    }

    .div-row div i {
        margin-top: 1px;
    }

    .div-task-container {
        padding-left: 20px;
        padding-right: 0px;
    }

    .job-title {
        display: block;
        background: var(--default-app-alt-line-color);
        padding: 0px 2%;
    }

    .span-outside-job-title {
        padding: 0px 2%;
        margin-top: 5px;
        display: block;
    }

    .div-activity-row {
        display: grid;
        grid-template-columns: 58% 38%;
    }
}
</style>